import cn from 'classnames';
import parse from 'html-react-parser';
import { FC } from 'react';
import { match } from 'ts-pattern';

import { Button } from '@catawiki/cata-style';

import {
  BackgroundClassMap,
  COLOR_NAME_BRAND,
} from '@/shared/constants/colors';
import { BuilderString, CtaVariant, LinkTarget } from '@/shared/types';

import {
  cleanBuilderParagraphInput,
  cleanBuilderStringInput,
  getBuilderString,
  isBuilderStringPresent,
} from '../../text';
import { FlatMediaInput, Media, MediaType } from '../Media';
import styles from './Row.module.scss';
import { RowTitleTypography } from './types';

type RowTitle = {
  title: BuilderString;
  titleTypography: RowTitleTypography;
  locale: string;
  spacingClassName?: string;
};

const RowTitle: FC<RowTitle> = ({
  title,
  titleTypography,
  locale,
  spacingClassName = 'u-m-b-lg-xl u-m-b-md-m u-m-b-sm-m',
}) => {
  if (!isBuilderStringPresent(title, locale)) {
    return null;
  }

  return match(titleTypography)
    .with('h2', () => (
      <div
        data-testid="content-image-row-title"
        className={cn('u-typography-h2', spacingClassName)}
      >
        {parse(cleanBuilderStringInput(getBuilderString(title, locale)))}
      </div>
    ))
    .with('h3', () => (
      <div
        data-testid="content-image-row-title"
        className={cn('u-typography-h3', spacingClassName)}
      >
        {parse(cleanBuilderStringInput(getBuilderString(title, locale)))}
      </div>
    ))
    .exhaustive();
};

export interface RowInput extends FlatMediaInput {
  title: BuilderString;
  description: BuilderString;
  ctaUrl?: string;
  ctaText: string;
  ctaColor?: string;
  ctaTarget?: LinkTarget;
}

interface Props extends RowInput {
  className?: string;
  ctaVariant: CtaVariant;
  rowTitleTypography: RowTitleTypography;
  locale: string;
}

export const Row: FC<Props> = ({
  className,
  mediaType = MediaType.IMAGE,
  mediaCaption,
  imageUrl,
  imageLink,
  imageLinkTarget,
  videoUrl,
  videoAutoPlay,
  youtubeUrl,
  youtubeDisabledDialog,
  locale,
  title,
  rowTitleTypography = 'h3',
  description,
  ctaUrl,
  ctaVariant,
  ctaText,
  ctaColor = COLOR_NAME_BRAND,
  ctaTarget = LinkTarget.NEW_WINDOW,
}) => {
  const fullWidth = ctaVariant !== 'tertiary';
  const showCta = ctaVariant !== 'none';

  return (
    <div
      data-testid="content-image-row"
      className={cn('tw:flex', styles.container, className)}
    >
      <Media
        className={styles.imageContainer}
        containerClassName={styles.image}
        locale={locale}
        mediaType={mediaType}
        mediaCaption={mediaCaption}
        image={imageUrl}
        imageLink={imageLink}
        imageLinkTarget={imageLinkTarget}
        video={{
          source: videoUrl || '',
          autoPlay: videoAutoPlay,
          poster: imageUrl,
        }}
        youtube={{
          url: youtubeUrl || '',
          poster: imageUrl || '',
          disabledDialog: youtubeDisabledDialog,
        }}
      />
      <div
        className={cn(
          styles.content,
          'tw:flex',
          'tw:flex-col',
          'tw:justify-center'
        )}
      >
        <RowTitle
          title={title}
          titleTypography={rowTitleTypography}
          locale={locale}
          spacingClassName={
            isBuilderStringPresent(description, locale) || showCta
              ? 'u-m-b-lg-xl u-m-b-md-m u-m-b-sm-m'
              : ''
          }
        />
        {isBuilderStringPresent(description, locale) ? (
          <div
            data-testid="content-image-row-description"
            className={cn('u-typography-h5', 'tw:text-neutral-secondary', {
              'u-m-b-lg-xl u-m-b-md-m u-m-b-sm-m': showCta,
            })}
          >
            {parse(
              cleanBuilderParagraphInput(getBuilderString(description, locale))
            )}
          </div>
        ) : null}
        {showCta && (
          <div className={cn({ [styles.fullWidth]: fullWidth })}>
            <Button
              className={cn({
                [BackgroundClassMap[ctaColor]]: ['primary'].includes(
                  ctaVariant
                ),
              })}
              as="a"
              href={cleanBuilderStringInput(
                getBuilderString(ctaUrl, locale),
                true
              )}
              variant={ctaVariant}
              target={ctaTarget}
            >
              {parse(
                cleanBuilderStringInput(getBuilderString(ctaText, locale))
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
